/**
 * Authentication Service using OIDC (OpenID Connect)
 * Handles OAuth flow, token management, and user profile
 */

import { UserManager, User } from 'oidc-client-ts';
import { oidcConfig } from '../config/oauthConfig.ts';
import { jwtDecode } from 'jwt-decode';

// Create singleton UserManager instance
const userManager = new UserManager(oidcConfig);

// Enable logging for debugging (disable in production)
if (process.env.NODE_ENV === 'development') {
  console.log('[AuthService] OAuth config:', oidcConfig);
}

/**
 * JWT Token Claims Interface
 */
interface TokenClaims {
  sub?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  email?: string;
  email_verified?: boolean;
  organization?: string;
  exp?: number;
  iat?: number;
}

/**
 * User Profile Interface
 */
export interface UserProfile {
  id: string;
  username: string;
  name: string;
  email: string;
  organization?: string;
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  tokenExpiry: number;
}

class AuthService {
  private userManager: UserManager;

  constructor() {
    this.userManager = userManager;

    // Setup event handlers
    this.userManager.events.addUserLoaded((user) => {
      console.log('[AuthService] User loaded:', user.profile);
      console.log('[AuthService] Token expires at:', new Date(user.expires_at! * 1000).toISOString());
    });

    this.userManager.events.addUserUnloaded(() => {
      console.log('[AuthService] User unloaded');
    });

    this.userManager.events.addAccessTokenExpiring(() => {
      console.log('[AuthService] Access token expiring - attempting auto-renewal...');
    });

    this.userManager.events.addAccessTokenExpired(() => {
      console.warn('[AuthService] Access token expired! User will need to re-login.');
      // Try one last refresh attempt
      this.refreshToken().catch(() => {
        console.error('[AuthService] Failed to refresh expired token');
      });
    });

    this.userManager.events.addSilentRenewError((error) => {
      console.error('[AuthService] Silent renew error:', error);
      // Optionally: notify user or trigger re-login
    });

    this.userManager.events.addUserSignedOut(() => {
      console.log('[AuthService] User signed out - clearing local session');
      this.clearSession();
    });
  }

  /**
   * Initiate OAuth login - redirects to Keycloak
   */
  async login(): Promise<void> {
    try {
      console.log('[AuthService] Starting OAuth login flow...');
      console.log('[AuthService] Redirect URI:', oidcConfig.redirect_uri);
      console.log('[AuthService] Client ID:', oidcConfig.client_id);
      await this.userManager.signinRedirect({
        state: { returnUrl: window.location.pathname }
      });
    } catch (error) {
      console.error('[AuthService] Login error:', error);
      throw error;
    }
  }

  /**
   * Handle OAuth callback after successful authentication
   */
  async handleCallback(): Promise<User> {
    try {
      console.log('[AuthService] Handling OAuth callback...');
      console.log('[AuthService] Current URL:', window.location.href);

      const user = await this.userManager.signinRedirectCallback();

      console.log('[AuthService] Callback successful!');
      console.log('[AuthService] User profile:', user.profile);
      console.log('[AuthService] Access token expires in:', user.expires_in, 'seconds');

      return user;
    } catch (error: any) {
      console.error('[AuthService] Callback error:', error);
      console.error('[AuthService] Error details:', {
        message: error.message,
        error: error.error,
        error_description: error.error_description
      });
      throw error;
    }
  }

  /**
   * Logout user and clear tokens
   */
  async logout(): Promise<void> {
    try {
      console.log('[AuthService] Logging out...');
      await this.userManager.signoutRedirect();
    } catch (error) {
      console.error('[AuthService] Logout error:', error);
      // Clear local state even if remote logout fails
      await this.userManager.removeUser();
      throw error;
    }
  }

  /**
   * Get currently authenticated user
   */
  async getUser(): Promise<User | null> {
    try {
      const user = await this.userManager.getUser();
      return user && !user.expired ? user : null;
    } catch (error) {
      console.error('[AuthService] Get user error:', error);
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const user = await this.getUser();
    return user !== null && !user.expired;
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    const user = await this.getUser();
    return user?.access_token || null;
  }

  /**
   * Get ID token
   */
  async getIdToken(): Promise<string | null> {
    const user = await this.getUser();
    return user?.id_token || null;
  }

  /**
   * Parse user profile from tokens
   */
  async getUserProfile(): Promise<UserProfile | null> {
    const user = await this.getUser();
    if (!user) return null;

    try {
      // Decode ID token to get user claims
      const idTokenClaims = user.profile as TokenClaims;

      // Extract user information
      const username = idTokenClaims.preferred_username ||
                      idTokenClaims.email?.split('@')[0] ||
                      idTokenClaims.sub ||
                      'unknown';

      const name = idTokenClaims.name ||
                  idTokenClaims.given_name ||
                  username;

      const profile: UserProfile = {
        id: idTokenClaims.sub || '',
        username: username,
        name: name,
        email: idTokenClaims.email || '',
        organization: idTokenClaims.organization,
        accessToken: user.access_token,
        refreshToken: user.refresh_token,
        idToken: user.id_token,
        tokenExpiry: user.expires_at || 0,
      };

      return profile;
    } catch (error) {
      console.error('[AuthService] Error parsing user profile:', error);
      return null;
    }
  }

  /**
   * Manually refresh access token
   */
  async refreshToken(): Promise<User | null> {
    try {
      console.log('[AuthService] Manually refreshing token...');
      const currentUser = await this.getUser();
      if (currentUser) {
        console.log('[AuthService] Current token expires at:', new Date(currentUser.expires_at! * 1000).toISOString());
      }

      const user = await this.userManager.signinSilent();

      console.log('[AuthService] ✅ Token refreshed successfully!');
      console.log('[AuthService] New token expires at:', new Date(user.expires_at! * 1000).toISOString());
      return user;
    } catch (error: any) {
      console.error('[AuthService] ❌ Token refresh failed:', error);
      console.error('[AuthService] Error details:', {
        message: error.message,
        name: error.name,
        error: error.error,
        error_description: error.error_description
      });
      return null;
    }
  }

  /**
   * Clear user session (local only)
   */
  async clearSession(): Promise<void> {
    await this.userManager.removeUser();
  }

  /**
   * Get time until token expires (in seconds)
   */
  async getTokenExpiryTime(): Promise<number> {
    const user = await this.getUser();
    if (!user || !user.expires_at) return 0;

    const now = Math.floor(Date.now() / 1000);
    const expiresIn = user.expires_at - now;
    return Math.max(0, expiresIn);
  }

  /**
   * Start monitoring token expiry and auto-refresh
   * This is a fallback mechanism in addition to automaticSilentRenew
   */
  startTokenMonitoring(checkIntervalSeconds: number = 30): () => void {
    const intervalId = setInterval(async () => {
      try {
        const user = await this.getUser();
        if (!user) return;

        const expiryTime = await this.getTokenExpiryTime();

        // If token expires in less than 2 minutes, try to refresh
        if (expiryTime > 0 && expiryTime < 120) {
          console.log(`[AuthService] Token expires in ${expiryTime}s - refreshing...`);
          await this.refreshToken();
        }
      } catch (error) {
        console.error('[AuthService] Token monitoring error:', error);
      }
    }, checkIntervalSeconds * 1000);

    console.log(`[AuthService] Token monitoring started (checking every ${checkIntervalSeconds}s)`);

    // Return cleanup function
    return () => {
      clearInterval(intervalId);
      console.log('[AuthService] Token monitoring stopped');
    };
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;
